import { type MutableRefObject, useEffect, useRef } from 'react'
import {
  notifyShapeTextFramesChanged,
  registerShapeTextFrame,
} from '@/components/app/shapes/shape-text-frame-registry'
import { type ShapeTextFrameHandleDeps, createShapeTextFrameHandle } from '../helpers/frame-handle'
import type { ShapeTextFonts } from '../types/shape-text-layout-state'

/**
 * Publish this frame to the cross-frame selection registry (WMPCM-9199).
 *
 * Every frame registers, not just the editing one: the whole point is that a drag started in a
 * sibling needs to resolve a caret *here*, and only this instance holds the layout to do it
 * with. The handle closes over refs so the registration itself does not need re-running on
 * every layout change.
 */
export function useShapeTextFrameRegistration({
  deps,
  overlayRef,
  fontsRef,
  setTextValue,
  lastEmittedModelTextRef,
}: {
  deps: ShapeTextFrameHandleDeps
  overlayRef: MutableRefObject<HTMLDivElement | null>
  fontsRef: MutableRefObject<ShapeTextFonts>
  setTextValue: (value: string) => void
  lastEmittedModelTextRef: MutableRefObject<string | null>
}) {
  const frameHandleDepsRef = useRef(deps)

  frameHandleDepsRef.current = deps

  const shapeId = deps.shape.id
  const { textValue, editingCaretIndex, showsOverflowBadge } = deps
  const { consumedLength } = deps.layout

  // Tell followers of the registry (the spell check) when what this frame holds changes. The handle
  // reads through `depsRef` and never changes identity, so registering again would say nothing.
  useEffect(() => {
    notifyShapeTextFramesChanged()
  }, [textValue, editingCaretIndex, showsOverflowBadge, consumedLength])

  useEffect(
    () =>
      registerShapeTextFrame(
        shapeId,
        createShapeTextFrameHandle({
          depsRef: frameHandleDepsRef,
          fontsRef,
          overlayRef,
          setTextValue,
          lastEmittedModelTextRef,
        })
      ),
    [setTextValue, shapeId, fontsRef, overlayRef, lastEmittedModelTextRef]
  )
}
